/**
 * MatterportViewer — embeds a 3D property tour via the Matterport
 * "Show" iframe. Consults /api/matterport/listing/{lid} first to make
 * sure the landlord still has quota; if not, renders an upgrade prompt
 * instead of the iframe. Records one billable embed_view event as soon
 * as the iframe finishes loading.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Lock } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function MatterportViewer({ listingId, className = "" }) {
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const billedRef = useRef(false);

  useEffect(() => {
    if (!listingId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/matterport/listing/${listingId}`);
        const d = await r.json();
        if (!cancelled) setTour(d);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listingId]);

  const src = useMemo(() => {
    if (!tour?.model_id || !tour?.sdk_key) return "";
    const params = new URLSearchParams({
      m: tour.model_id,
      applicationKey: tour.sdk_key,
      play: "1",
      qs: "1",
    });
    return `https://my.matterport.com/show/?${params.toString()}`;
  }, [tour]);

  async function handleLoaded() {
    if (billedRef.current || !tour?.model_id) return;
    billedRef.current = true;
    try {
      await fetch(`${API}/api/matterport/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listing_id: listingId,
          model_id: tour.model_id,
          event_type: "embed_view",
        }),
      });
    } catch { /* usage log best-effort */ }
  }

  if (loading) {
    return (
      <div className={`rounded-2xl border border-slate-200 bg-slate-50 aspect-video flex items-center justify-center text-sm text-slate-400 ${className}`}>
        Loading 3D tour…
      </div>
    );
  }
  if (failed || !tour?.model_id) return null;

  if (!tour.embed_allowed) {
    const isQuotaExceeded = tour.reason === "quota_exceeded";
    return (
      <div
        data-testid="matterport-blocked"
        className={`rounded-2xl border border-amber-200 bg-amber-50 p-5 flex flex-col sm:flex-row items-start gap-4 ${className}`}
      >
        <div className="rounded-xl bg-amber-100 p-3 shrink-0">
          <Lock className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1 text-sm">
          <div className="font-display text-base font-bold text-amber-900">
            {isQuotaExceeded ? "3D tour views used up this month" : "3D tour unavailable"}
          </div>
          <p className="mt-1 text-amber-800">
            {isQuotaExceeded
              ? `This property owner has used their ${tour.quota} included 3D tour views for the month. The tour will re-enable next billing cycle, or once they upgrade their plan.`
              : "The listing owner needs an active subscription to publish 3D tours."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl overflow-hidden border border-slate-200 bg-slate-950 ${className}`} data-testid="matterport-viewer">
      <div className="px-4 py-2 bg-slate-900 text-white text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
        <Box className="w-3.5 h-3.5" /> 3D walkthrough {tour.title ? `— ${tour.title}` : ""}
      </div>
      <iframe
        title="Matterport 3D tour"
        src={src}
        onLoad={handleLoaded}
        className="w-full aspect-video"
        allow="fullscreen; vr; xr-spatial-tracking; accelerometer; gyroscope"
        allowFullScreen
      />
    </div>
  );
}
